import type { ElementSettingsFormField } from '../../elements/types';
import { isRecord } from '../../../../../utils/typeGuards';

function getOrCreateNestedRecord(
    target: Record<string, unknown>,
    segment: string
): Record<string, unknown> {
    const existing = target[segment];
    if (isRecord(existing)) {
        return existing;
    }

    const created: Record<string, unknown> = {};
    target[segment] = created;

    return created;
}

export function getValueAtPath(source: unknown, path: string): unknown {
    return path.split('.').reduce<unknown>((value, segment) => {
        if (!isRecord(value)) {
            return undefined;
        }

        return value[segment];
    }, source);
}

export function setValueAtPath(
    target: Record<string, unknown>,
    path: string,
    value: unknown
): void {
    const segments = path.split('.');
    let cursor: Record<string, unknown> = target;

    segments.forEach((segment, index) => {
        const isLeaf = index === segments.length - 1;
        if (isLeaf) {
            cursor[segment] = value;
            return;
        }

        const next = cursor[segment];
        cursor = isRecord(next) ? next : getOrCreateNestedRecord(cursor, segment);
    });
}

export function deleteValueAtPath(target: Record<string, unknown>, path: string): void {
    const segments = path.split('.');
    let cursor: Record<string, unknown> = target;

    for (let index = 0; index < segments.length - 1; index += 1) {
        const next = cursor[segments[index]];
        if (!isRecord(next)) {
            return;
        }

        cursor = next;
    }

    delete cursor[segments[segments.length - 1]];
}

export function getRowsFieldItemFields(
    field: ElementSettingsFormField
): readonly ElementSettingsFormField[] {
    if (field.type !== 'rows') {
        return [];
    }

    const rowFields = field.fields;
    if (!rowFields || !rowFields.length) {
        throw new Error(`Expected rows field "${field.name}" to define at least one row field.`);
    }

    function flattenRowFields(
        fields: readonly ElementSettingsFormField[],
        prefix: string
    ): ElementSettingsFormField[] {
        return fields.reduce<ElementSettingsFormField[]>((flattenedFields, rowField) => {
            if (rowField.type === 'rows' || rowField.type === 'definitions') {
                throw new Error(
                    `Unsupported nested row field type "${rowField.type}" on "${rowField.name}".`
                );
            }

            const name = prefix.length ? `${prefix}.${rowField.name}` : rowField.name;
            if (rowField.type !== 'group') {
                return [...flattenedFields, { ...rowField, name }];
            }

            const nestedFields = rowField.fields;
            if (!nestedFields || !nestedFields.length) {
                throw new Error(`Expected group field "${name}" to define at least one row field.`);
            }

            return [...flattenedFields, ...flattenRowFields(nestedFields, name)];
        }, []);
    }

    return flattenRowFields(rowFields, '');
}
